/**
 * 사용자 카드 컴포넌트를 생성하고, 사용자 카드 컴포넌트에 대한 이벤트를 등록 및 제거합니다.
 */

import Vue from 'vue';
import { Component, Ref } from 'vue-property-decorator';
import { getGUID } from '@appbase/util';
import { i18n } from '@appbase/util/i18n-setup';
import store from '@/store';
import { mapGetters, mapMutations } from 'vuex';
import * as types from '@appbase/store/types'

import UserCard from './UserCard.vue';
import UserCardExtension from './UserCardExtension.vue';

export enum CardOpenMethod { MouseOver, RightClick, Click }
export enum CardComponentType { Default, Extension }

interface InstanceDictionary {
    [key: string]: HTMLElement;
}

@Component({
    computed: mapGetters({
        userCard: types.USERCARD_CURRENT
    }),

    methods: {
        ...mapMutations({
            setUserCard: types.USERCARD_CURRENT
        })
    }
})
export default class UserCardComponent extends Vue {  
    setUserCard!: (payload: Vue) => void;

    userCard!: Vue | undefined;

    openMethod: CardOpenMethod = CardOpenMethod.MouseOver;
    componentType: CardComponentType = CardComponentType.Default;
    identityApiUrl: string = '/hr/Person/api/detail'
    elemDictionary: { [key: string]: HTMLElement; } = {};

    contextHandlerDefault: any | null = null;
    hoverTimer: number | null = null;

    addEventToElement(target: HTMLElement | Vue) {
        let el: HTMLElement | null =  null;

        if((target as any).getAttribute) {
            el = target as HTMLElement;
        } else {
            el = ((target as Vue).$el) as HTMLElement; 
        }    

        if (el.getAttribute('data-usercard-eventmount') || el == null) return;

        // 이벤트 추가
        let dataGuid = getGUID(false);

        if(this.openMethod === CardOpenMethod.MouseOver) {
            el.addEventListener('mouseenter', this.mouseEnterHandler);
            el.addEventListener('mouseleave', this.mouseLeaveHandler);
        } else if(this.openMethod === CardOpenMethod.RightClick) {
            el.setAttribute('class', `usercard-elem-base`);
            el.addEventListener('contextmenu', this.mouseDownHandler);             
        } else{
            el.addEventListener('click', this.mouseDownHandler);             
        }

        el.setAttribute('data-usercard-eventmount', dataGuid);
        this.elemDictionary[dataGuid] = el;
    }


    removeEventFromElement(target: HTMLElement | Vue) { 
        let el: HTMLElement | null =  null;

        if((target as any).getAttribute) {
            el = target as HTMLElement;
        } else {
            el = ((target as Vue).$el) as HTMLElement; 
        }    

        if (!el.getAttribute('data-usercard-eventmount')) return;

        if(this.openMethod === CardOpenMethod.MouseOver) {
            el.removeEventListener('mouseenter', this.mouseEnterHandler);
            el.removeEventListener('mouseleave', this.mouseLeaveHandler);
        } else if(this.openMethod === CardOpenMethod.RightClick){     
            el.removeEventListener('contextmenu', this.mouseDownHandler)                                  
        } else{
            el.removeEventListener('click', this.mouseDownHandler)
        }

        el.removeAttribute('data-usercard-eventmount');                
    }

    mouseEnterHandler(e: MouseEvent) {     
        var targetKey = (e.target as Element).getAttribute('data-usercard-eventmount'); 

        if(this.userCard) {
            this.hoverTimer = setTimeout(() => {
                if(targetKey != null) {
                    var targetElem = this.elemDictionary[targetKey];
                    var personCode = targetElem.getAttribute('data-personcode');
                    var upn = targetElem.getAttribute('data-upn');
                    var email = targetElem.getAttribute('data-email');
    
                    if (personCode) {
                        (this.userCard as any).handleOpen(targetElem, personCode, 'personCode');
                    } else if (upn) {
                        (this.userCard as any).handleOpen(targetElem, upn, 'upn');
                    } else if (email) {
                        (this.userCard as any).handleOpen(targetElem, email, 'email');
                    }
                }    
            }, 500);                                             
        }                        
    }

    mouseDownHandler(e: MouseEvent) {
        e.preventDefault();

        var targetKey = (e.target as Element).getAttribute('data-usercard-eventmount');

        if (this.userCard) {
            if (targetKey != null) {
                var targetElem = this.elemDictionary[targetKey];
                var personCode = targetElem.getAttribute('data-personcode');
                var upn = targetElem.getAttribute('data-upn');
                var email = targetElem.getAttribute('data-email');

                if (personCode) {
                    (this.userCard as any).handleOpen(targetElem, personCode, 'personCode');
                } else if (upn) {
                    (this.userCard as any).handleOpen(targetElem, upn, 'upn');
                } else if (email) {
                    (this.userCard as any).handleOpen(targetElem, email, 'email');
                }
            }
        }
    }

    mouseLeaveHandler(e: MouseEvent) { 
        if(this.hoverTimer) {
            clearTimeout(this.hoverTimer);
        }  
    }

    mountEvents() {   
        for(let [key, value] of Object.entries(this.$refs))
        {
            if(/^refUserCardEl/.test(key) && value) {
                if(Array.isArray(value)) {
                    (value as HTMLElement[] | Vue[]).forEach(this.addEventToElement);
                } else {
                    let elem = value as HTMLElement | Vue;
                    this.addEventToElement(elem);
                }
            }            
        }        
    }

    removeEvents() {
        for(let [key, value] of Object.entries(this.$refs))
        {
            if(/^refUserCardEl/.test(key) && value) {
                if(Array.isArray(value)) {
                    (value as HTMLElement[] | Vue[]).forEach(this.removeEventFromElement);
                } else {
                    let elem = value as HTMLElement | Vue;
                    this.removeEventFromElement(elem);
                }
            }    
        }

        this.elemDictionary = {};
    }

    mounted() {
        this.$nextTick(() => {
            var cardElem = document.getElementById('eum-user-card');

            if(cardElem == null || this.userCard === undefined) {
                var CardComponent = this.componentType === CardComponentType.Default ? UserCard : UserCardExtension
                var UserCardClass = Vue.extend(CardComponent);
                var instance = new UserCardClass({
                    store,
                    i18n,
                    propsData: { openMethod: this.openMethod, identityApiUrl: this.identityApiUrl, addUser: this.addUser, sendAlarm: this.sendAlarm, addToTeam: this.addToTeam, showDetail: this.userDetail }
                });
                instance.$mount();
                document.body.append(instance.$el)
                this.setUserCard(instance);
            }

            this.mountEvents();              
        });
    }

    updated() {
        this.$nextTick(() => {
            this.mountEvents(); 
        });
    }

    beforeDestroy() {
        this.removeEvents();
    }

    addUser(user: account.User) {
    }

    sendAlarm(user: account.User) {
    }

    addToTeam(user: account.User) {
    }

    userDetail(user: account.User){
    }
}
